import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { type } from "arktype";
import { FilePlus2, Loader2 } from "lucide-react";
import { Suspense } from "react";
import {
  cardLimitIncreaseRequestValidator,
  formatCurrency,
  requestClientValidator,
} from "shared";
import { toast } from "sonner";
import type z from "zod";
import type { FileData } from "@/components/fields/file-upload-field";
import { RequestInfoFields } from "@/components/fields/request-info-fields";
import { GivefrontCardIcon } from "@/components/icons/credit-card-icon";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { Button } from "@/components/ui/button";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient, trpc } from "@/lib/trpcClient";

const validateSearch = type({
  cardId: "string",
});

const formValidator = cardLimitIncreaseRequestValidator.and(
  requestClientValidator,
);

export const Route = createFileRoute(
  "/_authed/_org/requests/create/limit-increase",
)({
  beforeLoad: async ({
    context: { queryClient, trpc },
    search: { cardId },
  }) => {
    if (!cardId) throw redirect({ to: "/cards" });
    await queryClient.ensureQueryData(trpc.card.get.queryOptions({ cardId }));
  },
  component: RouteComponent,
  validateSearch,
});

function RouteComponent() {
  const { cardId } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: card } = useSuspenseQuery(
    trpc.card.get.queryOptions({ cardId }),
  );

  const { mutate: request, isPending } = useMutation(
    trpc.request.create.mutationOptions({
      onError: (error) => {
        toast.error("Error", {
          description: error.message,
          position: "bottom-left",
        });
      },
      onSuccess: (newRequest) => {
        toast.success("Card limit increase requested", {
          position: "bottom-left",
        });
        navigate({ params: { id: newRequest.id }, to: "/requests/$id" });
        queryClient.invalidateQueries(trpc.request.list.queryOptions());
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      cardId: cardId,
      files: [] as FileData[],
      newTargetCents: card.restriction.periodTargetCents,
      reason: "",
    } as z.infer<typeof formValidator>,
    onSubmit: ({ value }) => {
      const { files, reason, ...data } = value;

      request({
        fileIds: files?.map((f) => f.id),
        reason,
        request: { data, type: "limit" },
      });
    },
    validators: {
      onSubmit: formValidator,
    },
  });

  return (
    <>
      <SheetHeader>
        <SheetTitle className="items-center gap-2 text-neutral-600 sm:flex">
          Request a higher limit for{" "}
          <span className="inline-flex items-center gap-1 text-neutral-800">
            <GivefrontCardIcon variant={card.type} className="size-8" />
            {card.name}
          </span>
        </SheetTitle>
        <SheetDescription>
          You can request a higher limit for this card by filling out the form
          below. If an admin in your organization approves it, it will be
          adjusted for you.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex min-h-0 grow flex-col"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <div className="flex grow flex-col gap-4 overflow-y-auto border-neutral-300 border-y p-6 pt-2">
            <FormStepSeparator description="Details" />

            <form.AppField name="newTargetCents">
              {(field) => (
                <field.MoneyField
                  description={`Current limit: ${formatCurrency(card.restriction.periodTargetCents)}`}
                  descriptionStyle="below"
                  label="New Spend Limit"
                />
              )}
            </form.AppField>

            <RequestInfoFields
              form={form}
              fields={{
                files: "files",
                reason: "reason",
              }}
            />
          </div>

          <SheetFooter className="px-6">
            <Button type="submit" className="self-start">
              {isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <FilePlus2 className="mr-2 size-4" />
              )}{" "}
              Request
            </Button>
          </SheetFooter>
        </Suspense>
      </form>
    </>
  );
}
